import type { RuntimeConfig } from './runtimeConfig';

export type DeploymentMode = 'cloud' | 'local-dev' | 'standin';

export type ResolveApiEndpointInput = {
  host: string;
  origin: string;
  pathname: string;
  apiEnvParam?: string;
  baseUrlParam?: string;
  runtimeConfig?: RuntimeConfig;
};

export type ResolvedApiEndpoint = {
  mode: DeploymentMode;
  apiEnv?: string;
  baseUrl?: string;
  webSocketUrl?: string;
  isStandinDeployment: boolean;
};

export const FEATURE_PATH_PATTERN = /feature\/([-\w]*)\/app\//;

export const ENV_PATH_PATTERN = /([a-z]+\d*)\/app\//;

export const LOCALHOST_PATTERN = /^(localhost|[0-9.]+):/;

export const DEV_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]', '::1'];

const hostnameOf = (host: string): string => host.toLowerCase().replace(/:\d+$/, '');

export function isDevServerHost(host: string): boolean {
  return DEV_HOSTNAMES.includes(hostnameOf(host)) || LOCALHOST_PATTERN.test(host);
}

export function resolveApiEndpoint({
  host,
  origin,
  pathname,
  apiEnvParam,
  baseUrlParam,
  runtimeConfig = {},
}: ResolveApiEndpointInput): ResolvedApiEndpoint {
  const featureMatch = pathname.match(FEATURE_PATH_PATTERN);
  const envMatch = pathname.match(ENV_PATH_PATTERN);
  const isDevHost = isDevServerHost(host);

  if (runtimeConfig.standinDeployment === true) {
    const baseUrl = baseUrlParam || runtimeConfig.apiBaseUrl || origin;
    return {
      mode: 'standin',
      baseUrl,
      webSocketUrl: runtimeConfig.webSocketUrl || baseUrl,
      isStandinDeployment: true,
    };
  }

  if (apiEnvParam) {
    return {
      mode: isDevHost ? 'local-dev' : 'cloud',
      apiEnv: apiEnvParam,
      baseUrl: baseUrlParam,
      isStandinDeployment: false,
    };
  }

  if (featureMatch) {
    return { mode: 'cloud', apiEnv: 'uat', baseUrl: baseUrlParam, isStandinDeployment: false };
  }

  if (envMatch) {
    return {
      mode: 'cloud',
      apiEnv: envMatch[1],
      baseUrl: baseUrlParam,
      isStandinDeployment: false,
    };
  }

  if (isDevHost) {
    return { mode: 'local-dev', baseUrl: baseUrlParam, isStandinDeployment: false };
  }

  return { mode: 'cloud', baseUrl: baseUrlParam, isStandinDeployment: false };
}

export default resolveApiEndpoint;
